/**
 * PLAN AND BILLING — what this shop is on, and what it costs.
 *
 * WHY IT IS NOT A STUB. A stub implies the work is a formality. Taking
 * money is the one part of a system where a half-finished implementation
 * can charge somebody twice, charge the wrong card, or take a payment it
 * never records — so the honest thing is a screen that shows the state and
 * refuses to pretend.
 *
 * ─── AND THEN IT PRETENDED THE OTHER WAY. 2026-09-18 ───────────────────
 *
 * This screen was written when charging a shop did not exist, and it said
 * so in a row typed by hand: "Charging this shop a subscription — not
 * built. Nothing here can take your money or anybody else's." By
 * 2026-09-18 that was false in the most expensive direction available.
 * server/routes/pro.ts runs ARBCreateSubscriptionRequest against
 * Authorize.net, #/pro is a live checkout at the shop's own stamped rate,
 * accounts.plan_ref holds the gateway's subscription id, and Manko Mobile
 * Auto had been paying every month since 23 August. The billing screen
 * was telling a shop that was being billed that nothing could bill it.
 *
 * Same fault as the process page's "What is still missing" card, and
 * found by the sweep that followed it: a NEGATIVE CLAIM typed by hand has
 * nothing that goes with the feature when the feature lands, so it keeps
 * rendering, word perfect, and nothing anywhere fails.
 *
 * SO NO ROW HERE IS TYPED ANY MORE. Taking a customer's card is read from
 * /api/config, which is the server saying whether a gateway exists on this
 * deployment. The shop's own subscription is read from the shop's own
 * record. A row that is derived cannot go stale: cancel the subscription
 * and the row changes by itself.
 *
 * WHAT plan_ref MEANS, because the honest answer has three states and not
 * two. Paid WITH a ref is a live subscription we hold. Paid with NO ref is
 * comped, or set up by hand at the gateway — which is exactly how Manko
 * was paying $99 while this table said free, and why tools/anet-subs.ts
 * exists at all: two systems held the same fact and neither could see the
 * other. Free is free. Naming the middle state is the whole value of the
 * row, so that nobody reads "no subscription" as "not being charged".
 */

import { configNow } from "../data/client.ts";
import { NOTICE_MIN_DAYS, SOLO_CENTS, SOLO_JOB_CAP, SOLO_STEPS_TO_CENTS } from "../domain/solo.ts";
import { db, api } from "../data/client.ts";
import { esc, mount } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";

const BOSS: Role[] = ["admin", "supervisor"];

export async function billingPage(role: Role = "viewer"): Promise<void> {
  if (!BOSS.includes(role)) {
    mount(`<div class="page"><header class="page-head">
      <a class="back" href="#/more">&larr; Everything else</a><h1>Plan and billing</h1></header>
      <div class="card pad"><p class="quiet">This one is kept to the owner.</p></div></div>`);
    return;
  }

  const cfg = configNow();
  const canCharge = cfg.features.payments;

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Plan and billing</h1>
      <p>What this shop is on, and what the system can currently do about money.</p></header>
    <div id="rv-plan"></div>
    <div id="billing"><div class="loading">Reading&hellip;</div></div>
    <div id="referrals"></div>
  </div>`);
  void rvPlanCard();
  void referralsCard();

  const host = document.getElementById("billing");
  if (!host) return;

  try {
    const sb = await db();
    const { data } = await sb.from("accounts")
      .select("id,name,email,phone,labour_rate,created_at,plan,plan_rate,plan_since,plan_ref,plan_tier,solo_step_on,plan_cancelled_at,plan_ends_on")
      .limit(1).maybeSingle();
    const shop = data as {
      name: string; email: string | null; phone: string | null;
      labour_rate: number | null; created_at: string;
      plan: string | null; plan_rate: number | null;
      plan_since: string | null; plan_ref: string | null;
      plan_tier?: string | null; solo_step_on?: string | null;
      plan_cancelled_at?: string | null; plan_ends_on?: string | null;
    } | null;

    /* Three states, not two — see the header. Paid with no subscription id
       is a real shop paying by some other route, and calling that "not
       charged" is the mistake this row was rewritten to stop making. */
    const paying = (shop?.plan ?? "free") !== "free";
    const held = paying && !!shop?.plan_ref;
    const day = (iso: string): string => new Date(iso).toLocaleDateString(
      "en-US", { day: "numeric", month: "long", year: "numeric" });

    /* SOLO (2026-10-05): $50 until 100 closed and paid jobs, then $149.
       The count is the same one server/tasks/solo-step-up.ts acts on, read
       from the shop's own jobs, so the owner sees how close the step is
       before the email arrives rather than only after. */
    const solo = paying && shop?.plan_tier === "solo";
    const planWord = solo ? "Solo" : "Pro";
    const cancelled = paying && !!shop?.plan_cancelled_at;
    const endsOn = shop?.plan_ends_on ? day(`${shop.plan_ends_on}T12:00:00`) : "the end of the time you paid for";
    let soloJobs: number | null = null;
    if (solo && shop?.plan_since) {
      const { count } = await sb.from("jobs").select("id", { count: "exact", head: true })
        .eq("status", "paid").eq("is_training", false).gte("created_at", shop.plan_since);
      soloJobs = count ?? null;
    }
    const soloLine = !solo || cancelled ? "" : shop?.solo_step_on
      ? ` Solo moves to $${SOLO_STEPS_TO_CENTS / 100} a month on ${esc(day(`${shop.solo_step_on}T12:00:00`))}, `
        + `as the email said. Cancel before then if you do not want it.`
      : ` Solo is Pro at $${SOLO_CENTS / 100} until ${SOLO_JOB_CAP} closed and paid jobs`
        + `${soloJobs != null ? `, and this shop has ${soloJobs}` : ""}. At ${SOLO_JOB_CAP} it moves to `
        + `$${SOLO_STEPS_TO_CENTS / 100} a month, with an email at least ${NOTICE_MIN_DAYS} days before the first charge.`;

    host.innerHTML = `
      <div class="card">
        <div class="card-head">This shop</div>
        <div class="pad">
          <div class="kv"><span>Name</span><b>${esc(shop?.name ?? "—")}</b></div>
          <div class="kv"><span>Billing email</span><b>${esc(shop?.email ?? "not set")}</b></div>
          <div class="kv"><span>On the system since</span><b>${
            shop ? esc(new Date(shop.created_at).toLocaleDateString("en-US",
              { month: "long", year: "numeric" })) : "—"}</b></div>
          <div class="kv"><span>Deployment</span><b>${esc(cfg.stage)}</b></div>
        </div>
      </div>

      <div class="card">
        <div class="card-head">What the system can do with money right now</div>
        <div class="row">
          <div class="row-main">
            <div class="row-title"><span class="num">Taking a card from a customer</span>
              <span class="pill ${canCharge ? "s-completed" : "s-cancelled"}">${
                canCharge ? "configured" : "not connected"}</span></div>
            <div class="row-sub">${canCharge
              ? "A gateway is configured on this deployment."
              : "No gateway on this deployment, so the portal cannot take a payment."}</div>
          </div>
        </div>
        <div class="row">
          <div class="row-main">
            <div class="row-title"><span class="num">Recording a payment by hand</span>
              <span class="pill s-completed">working</span></div>
            <div class="row-sub">Cash, check and transfer are recorded against the invoice,
              and that is what the reports count.</div>
          </div>
        </div>
        <div class="row">
          <div class="row-main">
            <div class="row-title"><span class="num">Charging this shop a subscription</span>
              <span class="pill ${paying ? "s-completed" : "s-cancelled"}">${
                held ? "on subscription" : paying ? "paid" : "not charged"
              }</span></div>
            <div class="row-sub">${cancelled
              ? `Cancelled on ${esc(day(shop!.plan_cancelled_at!))}. Nothing more will be charged. ${esc(planWord)} `
                + `stays on until ${esc(endsOn)}, then the shop moves to Free. Customers, jobs and invoices stay put.`
              : held
              ? `On ${esc(solo ? "Solo" : shop?.plan ?? "a plan")}${shop?.plan_rate
                  ? ` at $${esc(String(shop.plan_rate))} a month` : ""}, billed by Authorize.net `
                + `on subscription ${esc(shop?.plan_ref ?? "")}${shop?.plan_since
                  ? `, since ${esc(day(shop.plan_since))}` : ""}. One subscription per shop, `
                + `ever, so nothing here can start a second one.${soloLine}`
              : paying
                /* Matt Bolen, 2026-10-17: he paid Anthony by Cash App and this row
                   told him "comped". That word is ours, not the shop's: to the owner
                   reading it, it says nobody has his money. Paid outside a card
                   subscription is still paid, so it says paid. */
                ? `On ${esc(planWord)}, `
                  + `paid to Supercanic directly rather than by a card subscription here. `
                  + `Your account is fully on, and there is nothing to do on this screen.`
                : "Nothing is being charged to this shop. Go Pro starts a real subscription at "
                  + `the rate shown there, Pro at $${SOLO_STEPS_TO_CENTS / 100} or Solo at $${SOLO_CENTS / 100} until `
                  + `${SOLO_JOB_CAP} paid jobs, and only the owner can start one.`}</div>
          </div>
        </div>
        <!-- ─── THE DOOR THIS SENTENCE POINTS AT ────────────────────
             This card has said "Go Pro starts a real subscription at the
             rate shown there" since September, and there was no there.
             Nothing in the whole app linked to #/pro: the only ways in
             were typing the address or being sent the link by text
             (tools/text-pro-link.ts). A hash router cannot 404, so a shop
             owner guessing at it landed on the day screen.

             Found by tools/reach-check.mjs, which exists because of it.

             Only when the shop is not already paying: one subscription per
             shop, ever, and offering a second is how a shop ends up billed
             twice every month until somebody notices. -->
        <!-- ─── CANCEL. Anthony, 2026-10-05: "add cancel on the billing
             page then." The terms promised it here since 3 October. Two
             presses, the second one spelling out what happens, because a
             cancel by a stray thumb costs a shop its texting number. The
             server does the work and says the real end date
             (server/routes/pro.ts, /api/pro/cancel). -->
        <!-- CHANGE THE CARD. Anthony, 2026-10-22, for Brian Huey. Only a
             gateway subscription (digits in plan_ref) has a card to move;
             the form is on #/pro, which shows it to a shop already paying. -->
        ${paying && !cancelled && /^[0-9]+$/.test(shop?.plan_ref ?? "") ? `
          <a class="row" href="#/pro">
            <div class="row-main">
              <div class="row-title"><span class="num">Change the card</span></div>
              <div class="row-sub">Put the monthly bill on a different card. Nothing is charged.</div>
            </div>
          </a>` : ""}
        ${paying && !cancelled ? `
          <div class="row" id="cancel-row">
            <div class="row-main">
              <div class="row-title"><span class="num">Cancel the subscription</span></div>
              <div class="row-sub" id="cancel-say">Stops the next charge. You keep ${esc(planWord)} until the end
                of the time you already paid for, then the shop moves to Free. Nothing is deleted.</div>
              <div class="cancel-go">
                <button class="btn" id="cancel-ask">Cancel ${esc(planWord)}</button>
                <button class="btn danger" id="cancel-yes" hidden>Yes, cancel. Do not charge me again</button>
                <button class="btn" id="cancel-no" hidden>Keep it</button>
              </div>
              <div class="msg-slot" id="cancel-msg"></div>
            </div>
          </div>` : ""}
        ${paying ? "" : `
          <a class="row" href="#/pro">
            <div class="row-main">
              <div class="row-title"><span class="num">Go Pro</span></div>
              <div class="row-sub">Pro or Solo, what each costs, and the card. Only the
                owner can start one.</div>
            </div>
            <div class="row-side"><div class="when">&rarr;</div></div>
          </a>`}
      </div>

      <div class="card">
        <div class="card-head">What to watch, now that money moves</div>
        <div class="wt-trap">The merchant account taking a CUSTOMER's card has to be this shop's
          own. A payment routed into the wrong gateway is somebody else's money and a very bad
          afternoon, which is why the desk refuses to bill a card against another shop's
          account.</div>
        <div class="wt-trap">One subscription per shop, ever. It is checked before a card is
          charged, because two subscriptions on one shop is a shop paying twice every month
          until a person happens to notice.</div>
        <div class="wt-trap">A plan here and a subscription at the gateway are two records of the
          same fact. Manko was on a live subscription for weeks while this table said free. When
          the two disagree, ask the gateway and not the table.</div>
      </div>`;
    wireCancel(planWord);
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/** The two presses of Cancel, and what the server says back. */
function wireCancel(planWord: string): void {
  const ask = document.getElementById("cancel-ask") as HTMLButtonElement | null;
  const yes = document.getElementById("cancel-yes") as HTMLButtonElement | null;
  const no = document.getElementById("cancel-no") as HTMLButtonElement | null;
  const msg = document.getElementById("cancel-msg");
  if (!ask || !yes || !no) return;
  const say = (html: string) => { if (msg) msg.innerHTML = html; };
  ask.addEventListener("click", () => {
    ask.hidden = true; yes.hidden = false; no.hidden = false;
    say(`<p class="msg">Are you sure? You will not be charged again, and ${esc(planWord)} switches off
      at the end of the time you already paid for.</p>`);
  });
  no.addEventListener("click", () => { ask.hidden = false; yes.hidden = true; no.hidden = true; say(""); });
  yes.addEventListener("click", () => {
    yes.disabled = true; no.disabled = true; yes.textContent = "Cancelling…";
    void (async () => {
      try {
        const sb = await db();
        const { data: { session } } = await sb.auth.getSession();
        const r = await fetch(api("/api/pro/cancel"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
          body: "{}",
        });
        const out = await r.json() as { ok?: boolean; error?: string; ends_on?: string | null; plan?: string };
        if (!out.ok) {
          say(`<p class="msg bad">${esc(out.error ?? "That did not go through. Nothing changed.")}</p>`);
          yes.disabled = false; no.disabled = false; yes.textContent = "Yes, cancel. Do not charge me again";
          return;
        }
        const until = out.ends_on ? new Date(`${out.ends_on}T12:00:00`).toLocaleDateString(
          "en-US", { day: "numeric", month: "long", year: "numeric" }) : "the end of the time you paid for";
        const row = document.getElementById("cancel-row");
        if (row) row.innerHTML = `<div class="row-main"><div class="row-title"><span class="num">Cancelled</span>
          <span class="pill s-cancelled">no more charges</span></div>
          <div class="row-sub">${esc(out.plan ?? planWord)} stays on until ${esc(until)}, then the shop moves to Free.
          A confirmation is on its way by email.</div></div>`;
      } catch {
        say(`<p class="msg bad">We could not tell whether that went through. Please do not press it again.
          Ring us on (951) 904-2477 and we will check.</p>`);
      }
    })();
  });
}

/* ─── RV DESK: THE TRIAL, AND THE 24-HOUR "PAY FOR A YEAR" OFFER ─────────
 *
 * Anthony, 2026-09-26: a trial customer who pays for a year within 24
 * hours of starting gets two months free (10 x the monthly price, once).
 * The card shows only on an RV Desk account with a trial behind it. The
 * button shows only while the SERVER says the offer is open; the server
 * checks the clock again when it is pressed, so a page left open past
 * the 24 hours is refused, not charged. See server/routes/rvdesk-annual.ts.
 */
type RvPlan = {
  rv: boolean;
  trial?: {
    package_label: string | null; extra_rvs?: number; rentals?: number | null;
    interval: string | null; subscription: string | null;
    monthly: string | null; trial_started_at: string | null; first_charge_on: string | null;
    annual_paid_at: string | null; annual_paid: string | null;
  } | null;
  annual?: { open: boolean; closes_at: string | null; amount: string | null; full_year: string | null };
  rvs?: { may_change: boolean; unit: string; max_extra: number };
  error?: string;
};

async function rvBearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

async function rvPlanCard(): Promise<void> {
  const host = document.getElementById("rv-plan");
  if (!host) return;
  let d: RvPlan;
  try {
    const r = await fetch(api("/api/rvdesk/plan"), { headers: { Authorization: `Bearer ${await rvBearer()}` } });
    if (!r.ok) return;
    d = await r.json() as RvPlan;
  } catch { return; }
  const t = d.trial;
  if (!d.rv || !t) return;

  const day = (iso: string | null): string => iso
    ? new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("en-US",
      { month: "long", day: "numeric", year: "numeric", ...(iso.length === 10 ? { timeZone: "UTC" } : {}) })
    : "";
  const when = (iso: string | null): string => iso
    ? new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

  const status = t.interval === "annual"
    ? `Paid for a year: $${esc(t.annual_paid ?? "")} on ${esc(day(t.annual_paid_at))}. No monthly charges.`
    : t.interval === "annual_pending"
      ? "Your switch to yearly billing is going through."
      : `Free trial. Your card is first charged $${esc(t.monthly ?? "")} on ${esc(day(t.first_charge_on))}, then every 30 days.`;

  const a = d.annual;
  const offer = a?.open && a.amount ? `
      <div class="row">
        <div class="row-main">
          <div class="row-title"><span class="num">Two months free: pay for a year</span>
            <span class="pill s-completed">until ${esc(when(a.closes_at))}</span></div>
          <div class="row-sub">Pay $${esc(a.amount)} once for a full year instead of $${esc(a.full_year ?? "")}.
            Your monthly billing stops and the year renews at the same price. This offer is only open
            for the first 24 hours of your trial.</div>
          <div id="rv-annual-msg"></div>
          <button class="btn primary" id="rv-annual-go" type="button">Pay $${esc(a.amount)} for a year</button>
        </div>
      </div>` : "";

  /* ADDED RVs, $9 each a month past the package, from the next charge.
     The server prices it (rvPlan) and moves the ARB; this only asks. */
  const rvs = d.rvs?.may_change ? `
      <div class="row">
        <div class="row-main">
          <div class="row-title"><span class="num">Added RVs</span></div>
          <div class="row-sub">RVs past your package are $${esc(d.rvs.unit)} each a month, from your next
            charge. Never more than $499 a month: past that you are simply on unlimited. Texting is
            billed separately, by use.</div>
          <label class="row-sub">Added RVs
            <input id="rv-extra" type="number" min="0" max="${d.rvs.max_extra}" step="1" inputmode="numeric"
              value="${t.extra_rvs ?? 0}" style="width:6em;margin-left:8px"></label>
          <div id="rv-extra-msg"></div>
          <button class="btn" id="rv-extra-go" type="button">Update my plan</button>
        </div>
      </div>` : "";

  host.innerHTML = `
    <div class="card">
      <div class="card-head">RV Shop Desk plan</div>
      <div class="pad">
        <div class="kv"><span>Package</span><b>${esc(t.package_label ?? "RV Shop Desk")}</b></div>
        <div class="kv"><span>Billing</span><b>${status}</b></div>
        <div class="kv"><span>Texting</span><b>Optional, billed separately by use</b></div>
      </div>
      ${rvs}
      ${offer}
    </div>`;

  const xgo = document.getElementById("rv-extra-go") as HTMLButtonElement | null;
  const xmsg = document.getElementById("rv-extra-msg");
  xgo?.addEventListener("click", async () => {
    const n = (document.getElementById("rv-extra") as HTMLInputElement | null)?.value ?? "0";
    xgo.disabled = true;
    try {
      const r = await fetch(api("/api/rvdesk/rvs"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${await rvBearer()}` },
        body: JSON.stringify({ extra: n }),
      });
      const out = await r.json().catch(() => ({})) as { ok?: boolean; note?: string; advice?: string | null; error?: string; unchanged?: boolean };
      if (xmsg) {
        xmsg.innerHTML = out.ok
          ? `<div class="empty">${esc(out.unchanged ? "Nothing to change." : out.note ?? "Updated.")}${out.advice ? ` ${esc(out.advice)}` : ""}</div>`
          : `<div class="empty bad">${esc(out.error ?? "That did not go through.")}</div>`;
      }
      /* Re-read, so the Package line and the annual figure show the new amount. */
      if (out.ok && !out.unchanged) {
        const said = xmsg?.innerHTML ?? "";
        await rvPlanCard();
        const again = document.getElementById("rv-extra-msg");
        if (again) again.innerHTML = said;
        return;
      }
    } catch {
      if (xmsg) xmsg.innerHTML = `<div class="empty bad">That did not go through. Try again in a minute.</div>`;
    }
    xgo.disabled = false;
  });

  const go = document.getElementById("rv-annual-go") as HTMLButtonElement | null;
  const msg = document.getElementById("rv-annual-msg");
  go?.addEventListener("click", async () => {
    if (!confirm(`Charge $${a?.amount ?? ""} to the card on file now for a year of RV Shop Desk?`)) return;
    go.disabled = true;
    go.textContent = "Charging…";
    try {
      const r = await fetch(api("/api/rvdesk/annual"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${await rvBearer()}` },
        body: "{}",
      });
      const out = await r.json().catch(() => ({})) as { ok?: boolean; note?: string; error?: string };
      if (out.ok) {
        if (msg) msg.innerHTML = `<div class="empty">${esc(out.note ?? "Your year is paid.")}</div>`;
        go.remove();
        return;
      }
      if (msg) msg.innerHTML = `<div class="empty bad">${esc(out.error ?? "That did not go through.")}</div>`;
      /* 410 is the window closing, 409 a change already under way, and an
         unreadable gateway answer (502) must not be pressed again. */
      if ([409, 410, 502].includes(r.status)) go.remove();
      else { go.disabled = false; go.textContent = `Pay $${a?.amount ?? ""} for a year`; }
    } catch {
      if (msg) msg.innerHTML = `<div class="empty bad">We could not tell whether that went through. Please do not press again. Email hello@rvdesk.com and we will check.</div>`;
      go.remove();
    }
  });
}

/* ─── REFER A SHOP ───────────────────────────────────────────────────────
 *
 * Anthony, 2026-09-27: every Shop Desk gets a code. A shop that joins Pro
 * on it earns this shop $75 or a free month once its first renewal
 * clears. The rules are in src/domain/referral.ts; the server decides
 * everything and this only shows and asks. The payout queue below it is
 * for the platform owner alone; the server only sends it to them.
 */
type Ref = {
  id: string; status: "pending" | "earned" | "paid" | "void"; reward: "cash" | "month"; reward_line: string;
  shop: string; started_at: string; earned_at: string | null; due_on: string | null; paid_at: string | null;
  paid_note: string | null; void_reason: string | null;
  referrer?: string; referrer_email?: string | null; referrer_phone?: string | null;
};
type Refs = { code: string; link: string; may_month: boolean; referrals: Ref[]; queue: Ref[] | null; error?: string };

async function referralsCard(): Promise<void> {
  const host = document.getElementById("referrals");
  if (!host) return;
  let d: Refs;
  try {
    const r = await fetch(api("/api/referrals"), { headers: { Authorization: `Bearer ${await rvBearer()}` } });
    if (!r.ok) return;
    d = await r.json() as Refs;
  } catch { return; }

  const day = (iso: string | null): string => iso
    ? new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("en-US",
      { month: "short", day: "numeric", year: "numeric", ...(iso.length === 10 ? { timeZone: "UTC" } : {}) })
    : "";
  const state = (r: Ref): string => r.status === "pending"
    ? `On Pro since ${esc(day(r.started_at))}. Earned when they renew.`
    : r.status === "earned" ? `Renewed. ${esc(r.reward_line)} on its way by ${esc(day(r.due_on))}.`
    : r.status === "paid" ? `Paid ${esc(day(r.paid_at))}.${r.paid_note ? ` ${esc(r.paid_note)}` : ""}`
    : `Not owed. ${esc(r.void_reason ?? "")}`;
  const pill = (s: Ref["status"]): string =>
    `<span class="pill ${s === "paid" || s === "earned" ? "s-completed" : s === "void" ? "s-cancelled" : ""}">${s}</span>`;
  const pick = (r: Ref): string => r.status === "pending" || r.status === "earned" ? `
      <div class="row-sub">Your reward:
        <label><input type="radio" name="rw-${esc(r.id)}" value="cash" data-ref="${esc(r.id)}"${r.reward === "cash" ? " checked" : ""}> $75</label>
        ${d.may_month ? `<label><input type="radio" name="rw-${esc(r.id)}" value="month" data-ref="${esc(r.id)}"${
          r.reward === "month" ? " checked" : ""}> a free month of Pro</label>` : ""}
      </div>` : "";

  const mine = d.referrals.length
    ? d.referrals.map((r) => `
      <div class="row"><div class="row-main">
        <div class="row-title"><span class="num">${esc(r.shop)}</span> ${pill(r.status)}</div>
        <div class="row-sub">${state(r)}</div>${pick(r)}
      </div></div>`).join("")
    : `<div class="pad"><p class="quiet">Nobody yet. Send your link to a shop you think would use it.</p></div>`;

  const queue = d.queue ? `
    <div class="card">
      <div class="card-head">Referral payouts (platform owner)</div>
      ${d.queue.length ? d.queue.map((r) => `
        <div class="row"><div class="row-main">
          <div class="row-title"><span class="num">${esc(r.referrer ?? "?")}</span> for ${esc(r.shop)} ${pill(r.status)}</div>
          <div class="row-sub">${r.status === "earned"
            ? `Owes ${esc(r.reward_line)}, due ${esc(day(r.due_on))}.${r.reward === "month"
              ? " Refund their next monthly charge in Authorize.net." : ""}`
            : `Waiting on the renewal. On Pro since ${esc(day(r.started_at))}.`}
            ${esc([r.referrer_email, r.referrer_phone].filter(Boolean).join(", "))}</div>
          ${r.status === "earned" ? `<button class="btn primary" type="button" data-paid="${esc(r.id)}">Mark paid</button>` : ""}
          <button class="btn" type="button" data-void="${esc(r.id)}">Not owed</button>
          <div data-msg="${esc(r.id)}"></div>
        </div></div>`).join("") : `<div class="pad"><p class="quiet">Nothing owed and nothing waiting.</p></div>`}
    </div>` : "";

  host.innerHTML = `
    <div class="card">
      <div class="card-head">Refer a shop</div>
      <div class="pad">
        <p>When a shop joins Pro with your code and renews after its first month, you get $75${
          d.may_month ? " or a free month of Pro, your choice" : ""}. Paid within 32 days of their signup.</p>
        <div class="kv"><span>Your code</span><b>${esc(d.code)}</b></div>
        <div class="kv"><span>Your link</span><b style="word-break:break-all">${esc(d.link)}</b></div>
        <button class="btn" type="button" id="ref-copy">Copy link</button>
        <span id="ref-copied" class="quiet"></span>
      </div>
      ${mine}
    </div>
    ${queue}`;

  document.getElementById("ref-copy")?.addEventListener("click", async () => {
    const said = document.getElementById("ref-copied");
    try { await navigator.clipboard.writeText(d.link); if (said) said.textContent = " Copied."; }
    catch { if (said) said.textContent = " Could not copy. Select the link above."; }
  });

  const post = async (path: string, body: unknown): Promise<{ ok: boolean; error?: string }> => {
    try {
      const r = await fetch(api(path), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${await rvBearer()}` },
        body: JSON.stringify(body),
      });
      const out = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
      return { ok: !!out.ok, error: out.error };
    } catch {
      return { ok: false, error: "That did not go through. Try again in a minute." };
    }
  };

  host.querySelectorAll<HTMLInputElement>("input[data-ref]").forEach((el) => {
    el.addEventListener("change", async () => {
      const out = await post(`/api/referrals/${el.dataset["ref"]}/reward`, { reward: el.value });
      if (!out.ok) { alert(out.error ?? "That did not change."); await referralsCard(); }
    });
  });
  host.querySelectorAll<HTMLButtonElement>("button[data-paid]").forEach((b) => {
    b.addEventListener("click", async () => {
      const id = b.dataset["paid"] ?? "";
      const note = prompt("How was it paid? (Cash App, Zelle, check, refund)") ?? "";
      b.disabled = true;
      const out = await post(`/api/referrals/${id}/paid`, { note });
      if (out.ok) { await referralsCard(); return; }
      const m = host.querySelector(`[data-msg="${id}"]`);
      if (m) m.innerHTML = `<div class="empty bad">${esc(out.error ?? "That did not save.")}</div>`;
      b.disabled = false;
    });
  });
  host.querySelectorAll<HTMLButtonElement>("button[data-void]").forEach((b) => {
    b.addEventListener("click", async () => {
      const id = b.dataset["void"] ?? "";
      const reason = prompt("Why is it not owed?") ?? "";
      if (!reason.trim()) return;
      const out = await post(`/api/referrals/${id}/void`, { reason });
      if (out.ok) { await referralsCard(); return; }
      const m = host.querySelector(`[data-msg="${id}"]`);
      if (m) m.innerHTML = `<div class="empty bad">${esc(out.error ?? "That did not save.")}</div>`;
    });
  });
}
